import { PrimaryControlsStripLeading } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ToolbarMoreActions } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPopoverButton,
  ToolbarPopoverChoiceRow,
  type ToolbarPopoverChoiceOption,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import {
  ToolbarPillRow,
  ToolbarViewButton,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  BrowseWordsPageContext,
  type HskSortMode,
  type SortMode,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words._context";
import { BrowseWordsDisplayPushButton } from "#browse-words-display-push-button";

export interface BrowseWordsPageToolbarProps {
  onSortModeChange: (mode: SortMode) => void;
  sortMode: SortMode;
}

const HSK_SORT_OPTIONS: readonly ToolbarPopoverChoiceOption<HskSortMode>[] = [
  { label: "Frequency", testID: "words-sort-frequency", value: "frequency" },
  { label: "Strokes", testID: "words-sort-strokes", value: "strokes" },
  { label: "Learning", testID: "words-sort-learning", value: "learning" },
];

/**
 * Persistent left-side route pills for the Words tab. The containing page owns this portion of the
 * toolbar so it remains visible while a word view is loading.
 */
export function BrowseWordsPageToolbar({
  onSortModeChange,
  sortMode,
}: BrowseWordsPageToolbarProps) {
  return (
    <ToolbarPillRow contentContainerClassName="gap-0.5 p-1" hasLeadingActions>
      <ToolbarViewButton
        label="Frequency"
        onPress={() => onSortModeChange("frequency")}
        selected={sortMode === "frequency"}
      />
      <ToolbarViewButton
        label="HSK"
        onPress={() => onSortModeChange("hsk")}
        selected={sortMode === "hsk"}
      />
    </ToolbarPillRow>
  );
}

/**
 * Leading toolbar actions for the HSK Words view. The sort control is only relevant there (the
 * Frequency view has a single fixed ordering).
 */
/**
 * The Frequency Words view's More menu. A base page always shows its More menu, and Frequency has
 * no sort to offer, so it carries the shared Display settings.
 */
export function BrowseWordsFrequencyToolbarActions() {
  return (
    <PrimaryControlsStripLeading>
      <ToolbarMoreActions>
        <BrowseWordsDisplayPushButton label="Display" />
      </ToolbarMoreActions>
    </PrimaryControlsStripLeading>
  );
}

export function BrowseWordsHskToolbarActions() {
  const [hskSortMode, setHskSortMode] = useContextSelectors(
    BrowseWordsPageContext,
    (c) => c.hskSortMode,
    (c) => c.setHskSortMode,
  );
  return (
    <PrimaryControlsStripLeading>
      <ToolbarMoreActions>
        <BrowseWordsDisplayPushButton label="Display" />
        <ToolbarPopoverButton accessibilityLabel="Sort words" label="Sort" name="MdSort">
          <ToolbarPopoverChoiceRow
            closeOnSelect
            label="Sort by"
            layout="stacked"
            onValueChange={setHskSortMode}
            options={HSK_SORT_OPTIONS}
            value={hskSortMode}
          />
        </ToolbarPopoverButton>
      </ToolbarMoreActions>
    </PrimaryControlsStripLeading>
  );
}
